import { Indicator, Item, Root } from '@radix-ui/react-radio-group';
import clsx from 'clsx';
import type { FunctionComponent } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Input } from '~/ui/core/input/Input';

import { FormField } from '../components/FormField';

export type MeasureChoice = 'done' | 'none' | 'number' | 'percent';

export type NumberFields = {
    readonly start: string;
    readonly target: string;
    readonly unit: string;
};

const choices: readonly MeasureChoice[] = ['none', 'number', 'percent', 'done'];

const isMeasureChoice = (value: string): value is MeasureChoice => choices.some((choice) => choice === value);

const NumberInputs: FunctionComponent<{
    readonly numbers: NumberFields;
    readonly onChange: (numbers: NumberFields) => void;
}> = ({ numbers, onChange }) => {
    const translate = useTranslate();

    return (
        <div className="grid w-full grid-cols-3 gap-s">
            <FormField label={translate('pages.growth.form.number-start')}>
                {(id) => (
                    <Input
                        id={id}
                        placeholder="0"
                        type="number"
                        value={numbers.start}
                        onChange={(event) => onChange({ ...numbers, start: event.target.value })}
                    />
                )}
            </FormField>
            <FormField label={translate('pages.growth.form.number-target')}>
                {(id) => (
                    <Input
                        id={id}
                        placeholder="3"
                        type="number"
                        value={numbers.target}
                        onChange={(event) => onChange({ ...numbers, target: event.target.value })}
                    />
                )}
            </FormField>
            <FormField label={translate('pages.growth.form.number-unit')}>
                {(id) => (
                    <Input
                        id={id}
                        placeholder={translate('pages.growth.measure-help.number.unit-placeholder')}
                        value={numbers.unit}
                        onChange={(event) => onChange({ ...numbers, unit: event.target.value })}
                    />
                )}
            </FormField>
        </div>
    );
};

export const MeasurePicker: FunctionComponent<{
    readonly choice: MeasureChoice;
    readonly numbers: NumberFields;
    readonly onChoiceChange: (choice: MeasureChoice) => void;
    readonly onNumbersChange: (numbers: NumberFields) => void;
}> = ({ choice, numbers, onChoiceChange, onNumbersChange }) => {
    const translate = useTranslate();

    return (
        <div className="grid w-full grid-cols-[minmax(9rem,12rem)_minmax(0,1fr)] gap-m">
            <Root
                aria-label={translate('pages.growth.form.measure-label')}
                className="flex flex-col gap-xxs"
                orientation="vertical"
                value={choice}
                onValueChange={(value) => {
                    if (isMeasureChoice(value)) {
                        onChoiceChange(value);
                    }
                }}
            >
                {choices.map((value) => (
                    <Item
                        key={value}
                        className={clsx(
                            'group flex w-full cursor-pointer items-center gap-s rounded-md border-0 bg-transparent px-m py-s',
                            'text-left text-body-s text-text-secondary transition-colors hover:bg-surface-hover',
                            'data-[state=checked]:bg-surface-hover data-[state=checked]:font-medium data-[state=checked]:text-text-heading',
                            'outline-none focus-visible:ring-2 focus-visible:ring-border-strong',
                        )}
                        value={value}
                    >
                        <span className="flex size-[14px] shrink-0 items-center justify-center rounded-full border-[1.5px] border-border-control group-data-[state=checked]:border-border-strong">
                            <Indicator className="size-[6px] rounded-full bg-surface-inverse" />
                        </span>
                        {translate(`pages.growth.measure-types.${value}`)}
                    </Item>
                ))}
            </Root>
            <div
                aria-live="polite"
                className="flex min-w-0 flex-col gap-m rounded-md border border-border-subtle px-l py-m"
            >
                <div className="flex flex-col gap-xs">
                    <p className="m-0 text-body-s text-pretty text-text-heading">
                        {translate(`pages.growth.measure-help.${choice}.description`)}
                    </p>
                    <p className="m-0 text-body-xs text-pretty text-text-muted">
                        {translate('pages.growth.measure-help.example-prefix')}{' '}
                        {translate(`pages.growth.measure-help.${choice}.example`)}
                    </p>
                </div>
                {choice === 'number' ? <NumberInputs numbers={numbers} onChange={onNumbersChange} /> : null}
            </div>
        </div>
    );
};
